import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Megaphone } from "lucide-react";

const fmtM = (n) => {
  if (n >= 1_000_000) return (n / 1_000_000).toFixed(1).replace(/\.0$/, "") + "M";
  if (n >= 1_000) return (n / 1_000).toFixed(1).replace(/\.0$/, "") + "K";
  return String(n ?? 0);
};

const STATUSES = ["matched", "shared", "live", "declined"];

const AmplifyAdmin = () => {
  const [data, setData] = useState(null);
  const [notes, setNotes] = useState({});
  const [saving, setSaving] = useState(null);

  const load = () => api.get("/admin/amplify").then((r) => setData(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const update = async (req, status) => {
    setSaving(req.id);
    try {
      await api.post(`/admin/amplify/${req.id}`, { status, admin_note: notes[req.id] ?? req.admin_note ?? null });
      toast.success("Amplify request updated");
      await load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not update");
    } finally {
      setSaving(null);
    }
  };

  if (!data) return null;

  return (
    <div className="rounded-xl border border-border/50 bg-card p-5 mb-8" data-testid="admin-amplify">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2 text-sm font-semibold"><Megaphone className="w-4 h-4 text-primary" /> Amplify requests</div>
        <div className="text-xs text-muted-foreground">
          {data.total} total · {(data.by_status?.matched || 0)} matched · {(data.by_status?.shared || 0)} shared · {(data.by_status?.live || 0)} live
        </div>
      </div>

      {data.requests.length === 0 ? (
        <div className="text-sm text-muted-foreground py-6 text-center">No amplify submissions yet.</div>
      ) : (
        <div className="space-y-3">
          {data.requests.map((r) => (
            <div key={r.id} className="rounded-lg border border-white/5 bg-background p-4 flex flex-wrap items-center gap-4" data-testid={`admin-amplify-${r.id}`}>
              <div className="min-w-[200px] flex-1 flex items-start gap-3">
                {r.thumbnail_url && <img src={r.thumbnail_url} alt="" className="w-16 h-10 object-cover rounded border border-white/10 shrink-0" data-testid={`admin-amplify-thumb-${r.id}`} />}
                <div className="min-w-0">
                  <div className="font-semibold text-sm truncate">{r.title}</div>
                  <div className="text-xs text-muted-foreground truncate">{r.user_email} · {r.platform} · reach {fmtM(r.projected_reach)}</div>
                  {r.video_url && <a href={r.video_url} target="_blank" rel="noreferrer" className="text-xs text-primary hover:underline">Open video ↗</a>}
                  {r.requested_target && <div className="text-xs text-secondary mt-0.5">🎯 Requested: {r.requested_target.name} ({fmtM(r.requested_target.followers)})</div>}
                  {r.selected_creators?.length > 0 && <div className="text-xs text-primary mt-0.5">📣 Wants to share with: {r.selected_creators.map((c) => c.name).join(", ")}</div>}
                  {r.note && <div className="text-xs text-muted-foreground italic mt-1 truncate">"{r.note}"</div>}
                </div>
              </div>
              <Input value={notes[r.id] ?? r.admin_note ?? ""} onChange={(e) => setNotes({ ...notes, [r.id]: e.target.value })}
                placeholder="Note to subscriber" className="h-9 bg-transparent border-white/10 text-sm w-full sm:w-52" data-testid={`admin-amplify-note-${r.id}`} />
              <Select value={r.status} onValueChange={(v) => update(r, v)}>
                <SelectTrigger className="h-9 w-40 bg-transparent border-white/10 text-sm" data-testid={`admin-amplify-status-${r.id}`}><SelectValue /></SelectTrigger>
                <SelectContent>{STATUSES.map((s) => <SelectItem key={s} value={s} className="capitalize">{s}</SelectItem>)}</SelectContent>
              </Select>
              <Button size="sm" variant="outline" disabled={saving === r.id} onClick={() => update(r, r.status)}
                className="rounded-full border-white/15 text-xs" data-testid={`admin-amplify-save-${r.id}`}>Save note</Button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default AmplifyAdmin;
